import Papa from "papaparse";

/**
 * The "group" of an account is the leading token of its name
 * (e.g. "MYE CRYPTIC" -> "MYE", "FK DIVINE" -> "FK", "AJIO X" -> "AJIO").
 * Grouping is fully data-driven — every distinct prefix is its own group.
 */
export const groupForAccount = (name) =>
  String(name || "").trim().split(/\s+/)[0].toUpperCase() || "OTHER";

export const groupMatchesSelection = (accountName, selectedGroup) => {
  const group = groupForAccount(accountName);
  if (selectedGroup === "MYS+MYE") return group === "MYS" || group === "MYE";
  return group === selectedGroup;
};

/**
 * Aggregate scan-summary rows into per-group totals.
 * Only groups that actually have orders (total > 0) are returned.
 * Sorted by order count descending (biggest group first). Quantity is an item
 * detail, but scan progress and reconciliation are order-based.
 */
export function buildGroupSummary(scanSummary = []) {
  const map = new Map();
  scanSummary.forEach((row) => {
    const total = Number(row.total_orders ?? row.total_quantity ?? 0);
    if (total <= 0) return;
    const g = groupForAccount(row.account_name);
    const cur = map.get(g) || { group: g, total: 0, scanned: 0 };
    cur.total += total;
    cur.scanned += Number(row.scanned_entries ?? row.scanned_quantity ?? 0);
    map.set(g, cur);
  });
  const rows = [...map.values()]
    .map((x) => ({ ...x, remaining: x.total - x.scanned }))
    .sort((a, b) => b.total - a.total || a.group.localeCompare(b.group));

  // Myntra is split across two courier channels that are worked as one lot, so
  // add their combined subtotal. The dialogs treat MYS+MYE as a virtual group
  // that matches either account prefix. (The existing MY row is the DE channel.)
  const mys = rows.find((r) => r.group === "MYS");
  const mye = rows.find((r) => r.group === "MYE");
  if (mys && mye) {
    const total = mys.total + mye.total;
    const scanned = mys.scanned + mye.scanned;
    rows.push({ group: "MYS+MYE", total, scanned,
                remaining: total - scanned, combined: true });
  }

  // Keep the Myntra channels and their subtotal together, and put FK last —
  // they are separate platforms and reading them interleaved by size made the
  // Myntra figures harder to total up by eye.
  const fk = rows.filter((r) => r.group === "FK");
  const rest = rows.filter((r) => r.group !== "FK");
  return [...rest, ...fk];
}

/**
 * Distinct group codes present in an accounts list, sorted alphabetically.
 * Used to populate the "Group" dropdown in the not-scanned dialogs.
 */
export function groupsFromAccounts(accounts = []) {
  const set = new Set();
  accounts.forEach((a) => {
    const name = a?.name ?? a;
    if (name) set.add(groupForAccount(name));
  });
  const groups = [...set].sort((a, b) => a.localeCompare(b));
  if (set.has("MYS") && set.has("MYE")) {
    const myeIndex = groups.indexOf("MYE");
    groups.splice(myeIndex + 1, 0, "MYS+MYE");
  }
  return groups;
}

/**
 * Flatten not-scanned data (either flat rows for one account, or the
 * grouped [{account_name, orders:[]}] "all" shape) into CSV rows and
 * trigger a browser download. Opens directly in Excel.
 */
export function exportUnscannedCsv({
  nsOrderData = [],
  showAllAccounts = false,
  accountName = "",
  fileLabel = "all",
  date = "",
}) {
  let rows = [];
  if (showAllAccounts) {
    nsOrderData.forEach((g) => {
      (g.orders || []).forEach((o) =>
        rows.push({
          account_name: g.account_name,
          tracking_id: o.tracking_id,
          sku: o.sku,
          quantity: o.quantity,
        })
      );
    });
  } else {
    rows = nsOrderData.map((o) => ({
      account_name: o.account_name || accountName || "",
      tracking_id: o.tracking_id,
      sku: o.sku,
      quantity: o.quantity,
    }));
  }

  const csv = Papa.unparse(rows, {
    columns: ["account_name", "tracking_id", "sku", "quantity"],
  });
  const safeLabel = String(fileLabel || "all").replace(/[^\w.-]+/g, "_");
  const filename = `not-scanned_${safeLabel}${date ? `_${date}` : ""}.csv`;

  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
